import Phaser from 'phaser';
import { PocScene } from './adapters/phaser/poc-scene';
import { installPhase0CompatibilityApi } from './adapters/phaser/phase0-compat';
import { readVisualRegressionMode, resolveRendererMode } from './presentation';
import { installPhase5DebugApi, PHASE5_SCENES } from './presentation/scenes/phase5-scenes';
import { initializePhase6Services } from './application/phase6-services';
import { installPhase6DebugApi } from './application/phase6-debug';
import { getLandingPath, resolveRoute } from './routing/route';
import { applySeoMetadata, SEO_CONFIGS } from './routing/seo';
import { renderLandingView } from './presentation/landing/landing-view';
import { renderNotFoundView } from './presentation/not-found/not-found-view';
import { renderArticleView } from './presentation/articles/article-view';
import './style.css';

const visualRegressionMode = readVisualRegressionMode(globalThis.location.search);
const rendererMode = resolveRendererMode(visualRegressionMode);

const requireElement = (id: string): HTMLElement => {
  const element = document.getElementById(id);
  if (element === null) throw new Error(`Missing application element: ${id}`);
  return element;
};

const gameNavBar = requireElement('game-nav-bar');
const backToLandingLink = requireElement('back-to-landing-link');
const landingRoot = requireElement('landing-root');
const articleRoot = requireElement('article-root');
const gameContainer = requireElement('game-container');
const adElement = requireElement('ad-container');
const fatalElement = requireElement('fatal-error');
const notFoundRoot = requireElement('not-found-root');

let gameInstance: Phaser.Game | null = null;
let servicesInitialized = false;

const bootGameIfNeeded = (): void => {
  if (gameInstance !== null) {
    return;
  }
  if (!visualRegressionMode && !servicesInitialized) {
    initializePhase6Services(gameContainer, adElement, fatalElement);
    servicesInitialized = true;
  }
  gameInstance = new Phaser.Game({
    type: rendererMode === 'CANVAS' ? Phaser.CANVAS : Phaser.WEBGL,
    width: 1280,
    height: 720,
    parent: 'app',
    backgroundColor: '#172033',
    pixelArt: visualRegressionMode,
    banner: false,
    scene: visualRegressionMode ? [PocScene] : PHASE5_SCENES,
    scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
  });
  if (!visualRegressionMode) {
    installPhase0CompatibilityApi(rendererMode);
    installPhase5DebugApi(gameInstance);
    installPhase6DebugApi(gameInstance, adElement);
  }
};

const navigateTo = (path: string): void => {
  if (globalThis.location.pathname !== path) {
    globalThis.history.pushState({}, '', path);
  }
  renderCurrentRoute();
  globalThis.scrollTo({
    top: 0,
    left: 0,
    behavior: 'auto',
  });
  if (document.documentElement) {
    document.documentElement.scrollTop = 0;
  }
  if (document.body) {
    document.body.scrollTop = 0;
  }
};

const renderCurrentRoute = (): void => {
  const route = resolveRoute(globalThis.location.pathname, globalThis.location.search);
  applySeoMetadata(SEO_CONFIGS[route]);

  if (route === 'PLAY') {
    document.body.className = 'in-game';
    landingRoot.style.display = 'none';
    articleRoot.style.display = 'none';
    notFoundRoot.style.display = 'none';
    gameNavBar.style.display = visualRegressionMode ? 'none' : 'flex';
    gameContainer.style.display = 'block';
    bootGameIfNeeded();
    if (gameInstance !== null && gameInstance.isPaused) {
      gameInstance.isPaused = false;
      try { gameInstance.sound?.resumeAll(); } catch { /* noop */ }
    }
  } else if (route === 'LANDING') {
    document.body.className = 'in-landing';
    gameNavBar.style.display = 'none';
    gameContainer.style.display = 'none';
    adElement.style.display = 'none';
    fatalElement.style.display = 'none';
    articleRoot.style.display = 'none';
    notFoundRoot.style.display = 'none';
    landingRoot.style.display = 'block';
    if (gameInstance !== null && !gameInstance.isPaused) {
      gameInstance.isPaused = true;
      try { gameInstance.sound?.pauseAll(); } catch { /* noop */ }
    }
    renderLandingView(landingRoot, navigateTo);
  } else if (
    route === 'DESIGN' ||
    route === 'DEVELOPMENT' ||
    route === 'TESTING' ||
    route === 'ARCHITECTURE'
  ) {
    document.body.className = 'in-article';
    gameNavBar.style.display = 'none';
    gameContainer.style.display = 'none';
    adElement.style.display = 'none';
    fatalElement.style.display = 'none';
    landingRoot.style.display = 'none';
    notFoundRoot.style.display = 'none';
    articleRoot.style.display = 'block';
    if (gameInstance !== null && !gameInstance.isPaused) {
      gameInstance.isPaused = true;
      try { gameInstance.sound?.pauseAll(); } catch { /* noop */ }
    }
    renderArticleView(articleRoot, route, navigateTo);
  } else {
    document.body.className = 'in-not-found';
    gameNavBar.style.display = 'none';
    gameContainer.style.display = 'none';
    adElement.style.display = 'none';
    fatalElement.style.display = 'none';
    landingRoot.style.display = 'none';
    articleRoot.style.display = 'none';
    notFoundRoot.style.display = 'block';
    if (gameInstance !== null && !gameInstance.isPaused) {
      gameInstance.isPaused = true;
      try { gameInstance.sound?.pauseAll(); } catch { /* noop */ }
    }
    renderNotFoundView(notFoundRoot, navigateTo);
  }
};

const landingPath = getLandingPath();
backToLandingLink.setAttribute('href', landingPath);
backToLandingLink.setAttribute('data-nav', landingPath);
backToLandingLink.addEventListener('click', (event) => {
  event.preventDefault();
  navigateTo(landingPath);
});

globalThis.addEventListener('popstate', () => {
  renderCurrentRoute();
});

renderCurrentRoute();
